import { Component, OnInit, Input, EventEmitter, HostBinding, Output, ElementRef, HostListener } from '@angular/core';
import { Router } from '@angular/router';
import { NavService } from '../../../../../shared/services/nav.service';
import { TablerIconsModule } from 'angular-tabler-icons';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { Subject, takeUntil } from 'rxjs';
import { SideMenuService } from 'src/app/shared/services/sideMenu.service';
import { NavItem } from '../../../vertical/sidebar/nav-item/nav-item';
import { TranslateModule } from '@ngx-translate/core';
import { MaterialModule } from 'src/app/material.module';
import { trigger, state, style, transition, animate } from '@angular/animations';

@Component({
  selector: 'app-horizontal-nav-item',
  imports: [TablerIconsModule, CommonModule, MatIconModule, TranslateModule, MaterialModule],
  templateUrl: './nav-item.component.html',
  styleUrl: './nav-item.component.css',
  animations: [
    trigger('indicatorRotate', [
      state('collapsed', style({ transform: 'rotate(0deg)' })),
      state('expanded', style({ transform: 'rotate(180deg)' })),
      transition(
        'expanded <=> collapsed',
        animate('225ms cubic-bezier(0.4,0.0,0.2,1)')
      ),
    ]),
  ],
})
export class AppHorizontalNavItemComponent implements OnInit {
  @Output() toggleMobileLink: any = new EventEmitter<void>();
  @Output() notify: EventEmitter<boolean> = new EventEmitter<boolean>();

  expanded: any = false;
  disabled: any = false;
  twoLines: any = false;
  @HostBinding('attr.aria-expanded') ariaExpanded = this.expanded;
  @Input() item: NavItem | any;
  @Input() depth: any;
  langCode: any;
  isHeld = false;
  // Pin functionality
  isPinned: boolean = false;
  private destroy$ = new Subject<void>();
  private closeTimer: ReturnType<typeof setTimeout> | null = null;
  constructor(
    public navService: NavService,
    public router: Router,
    private menuService: SideMenuService,
    private el: ElementRef
  ) {
    if (this.depth === undefined) {
      this.depth = 0;
    }

    let lang: any = localStorage.getItem('lang');
    let langObj: any;
    if (lang != undefined && lang != null) {

      langObj = JSON.parse(lang);
      this.langCode = langObj.code;
    }
    else {
      this.langCode = 'ar';
    }

  }


  private positionDropdown(): void {
    if (!this.item?.children?.length) return;

    const host = this.el.nativeElement as HTMLElement;
    const rect = host.getBoundingClientRect();
    const vw = window.innerWidth;
    const isNested = host.classList.contains('ddmenu');
    const isRTL = document.documentElement.dir === 'rtl'
      || document.body.classList.contains('rtl');

    const ddW = isNested ? 256 : 268;
    const depth = this.depth ?? 0;
    host.style.setProperty('--dd-z', `${1200 + depth * 10}`);
    if (!isNested) {

      // ── Top-level: open below the nav item ────────────────────────────
      host.style.setProperty('--dd-top', `${rect.bottom}px`);
      if (!isRTL) {
        const fitsRight = rect.left + ddW <= vw;
        host.style.setProperty('--dd-left', fitsRight ? `${rect.left}px` : 'auto');
        host.style.setProperty('--dd-right', fitsRight ? 'auto' : `${vw - rect.right}px`);
        host.style.setProperty('--dd-origin', fitsRight ? 'top left' : 'top right');
      } else {
        host.style.setProperty('--dd-top', `${rect.top}px`);
        const fitsLeft = rect.right >= ddW;
        host.style.setProperty('--dd-right', fitsLeft ? `${vw - rect.right}px` : 'auto');
        host.style.setProperty('--dd-left', fitsLeft ? 'auto' : `${rect.left}px`);
        host.style.setProperty('--dd-origin', fitsLeft ? 'top right' : 'top left');
      }
    } else {
      // ── Nested: open to the side ──────────────────────────────────────
      host.style.setProperty('--dd-top', `${rect.top}px`);

      if (!isRTL) {
        const fitsRight = vw - rect.right >= ddW;
        host.style.setProperty('--dd-left', fitsRight ? `${rect.right}px` : 'auto');
        host.style.setProperty('--dd-right', fitsRight ? 'auto' : `${vw - rect.left}px`);
        host.style.setProperty('--dd-origin', fitsRight ? 'top left' : 'top right');
        // NEW — toggle bridge-side classes
        host.classList.toggle('opens-right', fitsRight);
        host.classList.toggle('opens-left', !fitsRight);
      } else {
        const fitsLeft = rect.left >= ddW;
        host.style.setProperty('--dd-right', fitsLeft ? `${vw - rect.left}px` : 'auto');
        host.style.setProperty('--dd-left', fitsLeft ? 'auto' : `${rect.right}px`);
        host.style.setProperty('--dd-origin', fitsLeft ? 'top right' : 'top left');
        // NEW — toggle bridge-side classes (RTL: "fitsLeft" means it opens toward the left)
        host.classList.toggle('opens-left', fitsLeft);
        host.classList.toggle('opens-right', !fitsLeft);
      }
    }
  }

  ngOnInit() {
    this.checkIfExpanded(this.router.url);
    this.updatePinStatus();

    if (this.item?.expanded === true) {
      this.expanded = true;
    }

    this.menuService.getPinnedItemsObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updatePinStatus();
      });
  }

  ngOnChanges() {
    const url = this.navService.currentUrl();
    if (this.item?.expanded === true) {
      this.expanded = true;
    }

    if (this.item.route && url) {
      this.expanded = url.indexOf(`/${this.item.route}`) === 0;
      this.ariaExpanded = this.expanded;
    }
  }

  onItemSelected(item: NavItem) {
    if (!item.children || !item.children.length) {
      this.router.navigate([item.route]);
    }
    if (item.children && item.children.length) {
      this.expanded = !this.expanded;
    }
    //scroll
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
    if (!this.expanded) {
      if (window.innerWidth < 1024) {
        this.notify.emit();
      }
    }
  }

  onSubItemSelected(item: NavItem) {
    if (!item.children || !item.children.length) {
      if (this.expanded && window.innerWidth < 1024) {
        this.notify.emit();
      }
    }
  }

  @HostListener('mouseenter')
  onMouseEnter(): void {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
    this.positionDropdown();
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    if (!this.item?.children?.length) return;
    this.closeTimer = setTimeout(() => {
      this.isHeld = false;
      this.closeTimer = null;
    }, 200);
  }

  onParentClick(event: Event): void {
    if (!this.item?.children?.length) return;
    event.preventDefault();
    event.stopPropagation();
    this.isHeld = !this.isHeld;
    if (this.isHeld) this.positionDropdown();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isHeld) return;
    const clickedInside = this.el.nativeElement.contains(event.target as Node);
    if (!clickedInside) this.isHeld = false;
  }


  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.closeTimer) clearTimeout(this.closeTimer);
  }


  checkIfExpanded(url: string) {
    this.expanded = this.hasActiveRoute(this.item, url);
    this.ariaExpanded = this.expanded;
  }

  updatePinStatus(): void {
    if (this.item?.id) {
      const wasPinned = this.isPinned;



      if (this.item.isPinned !== undefined) {
        this.isPinned = this.item.isPinned;
      } else {
        // Check service for active pinned item
        const servicePinnedItem = this.menuService.getPinnedItem(this.item.id);
        const isActivelyPinned = servicePinnedItem && servicePinnedItem.isActive !== false;

        this.isPinned = !!(isActivelyPinned && this.item.userSideMenuIsActive === true);
      }

      if (wasPinned !== this.isPinned) {
      }
    } else {
      this.isPinned = false;
    }
  }

  hasActiveRoute(item: NavItem, url: string): boolean {
    // Exact match for THIS item
    if (item.route && this.router.isActive(item.route, false)) {
      return true;
    }
    // Check all children
    if (item.children && item.children.length) {
      for (const child of item.children) {
        if (this.hasActiveRoute(child, url)) {
          return true; // If ANY child matches, parent must expand
        }
      }
    }

    return false;
  }

  shouldShowPinButton(): boolean {
    // Show pin button for ALL levels/depths - every menu item can be pinned
    return true;
  }

  togglePin(event: Event) {
    event.preventDefault();
    event.stopPropagation();

    if (!this.item?.id) {
      //  console.error('Item ID is missing');
      return;
    }

    if (this.item.isProcessingPin) {
      return;
    }


    this.item.isProcessingPin = true;

    // Use the most reliable source for current pin status
    const isCurrentlyPinned = this.item.isPinned === true ||
      (this.item.userSideMenuIsActive === true && this.menuService.isPinned(this.item.id));

    if (isCurrentlyPinned) {
      // UNPIN LOGIC - Find the active pinned record and deactivate it

      // Get the active pinned item record
      const pinnedItem = this.menuService.getPinnedItem(this.item.id);

      if (pinnedItem && pinnedItem.id) {

        this.menuService.unpinItemById(pinnedItem.id).subscribe({
          next: (response) => {

            // Update local state immediately for UI responsiveness
            this.isPinned = false;
            this.item.isPinned = false;
            this.item.userSideMenuIsActive = false;

            // Small delay to ensure API state is updated before reorganizing
            setTimeout(() => {
              // Force a menu reorganization to restore item to original position
              // This will be handled by the service's finalize() block
            }, 100);
          },
          error: (error) => {
            // console.error('Error unpinning item:', error);
            // Let the service refresh handle the final state
          },
          complete: () => {
            this.item.isProcessingPin = false;
          }
        });
      } else {
        // No active pinned record found, just update local state
        this.isPinned = false;
        this.item.isPinned = false;
        this.item.userSideMenuIsActive = false;
        this.item.isProcessingPin = false;
      }

    } else {
      // PIN LOGIC - Create new pinned record or reactivate existing inactive one

      this.menuService.pinItemById(this.item.id).subscribe({
        next: (response) => {

          // Update local state immediately for UI responsiveness
          this.isPinned = true;
          this.item.isPinned = true;
          this.item.userSideMenuIsActive = true;
        },
        error: (error) => {
          //  console.error('Error pinning item:', error);

          // Revert optimistic update on error
          this.isPinned = false;
          this.item.isPinned = false;
          this.item.userSideMenuIsActive = false;
        },
        complete: () => {
          this.item.isProcessingPin = false;
        }
      });
    }
  }
}
